import React from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { AdminKeyService } from '../../services/adminKeyService';

const ChatbotTab: React.FC = () => {
  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const openQr = async () => {
    setQrOpen(true);
    setLoading(true);
    setError(null);
    try {
      // Get admin token for PHP endpoint
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin token');
      // Request QR payload from PHP (server will return either a data URL or raw text we render)
      const res = await fetch('/api/whatsapp-init.php', {
        method: 'POST',
        headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || raw || 'Failed to initialize WhatsApp');
      }
      if (typeof json.qrDataUrl === 'string' && json.qrDataUrl.startsWith('data:image/')) {
        setQrDataUrl(json.qrDataUrl);
      } else if (typeof json.qrSvg === 'string' && json.qrSvg.includes('<svg')) {
        // Convert SVG text to data URL for <img>
        const svg64 = btoa(unescape(encodeURIComponent(json.qrSvg)));
        setQrDataUrl(`data:image/svg+xml;base64,${svg64}`);
      } else if (typeof json.qrText === 'string') {
        // Generate client-side QR using a fallback (simple google chart API)
        const enc = encodeURIComponent(json.qrText);
        setQrDataUrl(`https://chart.googleapis.com/chart?chs=300x300&cht=qr&chl=${enc}`);
      } else {
        throw new Error('No QR payload returned');
      }
    } catch (e: any) {
      setError(e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Chatbot
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Connect your WhatsApp to enable chatbot features.
      </Typography>

      <Card>
        <CardContent>
          <Button variant="contained" startIcon={<QrCode2Icon />} onClick={openQr} sx={{ textTransform: 'none' }}>
            Connect with WhatsApp
          </Button>
        </CardContent>
      </Card>

      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Scan QR Code</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Typography>Generating QR…</Typography>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : qrDataUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img src={qrDataUrl} alt="WhatsApp QR" style={{ width: '100%', maxWidth: 320 }} />
            </Box>
          ) : (
            <Typography color="text.secondary">No QR code yet.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatbotTab;
